import React, { useEffect, useMemo, useState } from 'react';
import API from '../api';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000';

function getElectionStatus(election) {
  if (!election) return 'none';
  const now = new Date();
  const start = new Date(election.start_at);
  const end = new Date(election.end_at);
  if (now < start) return 'upcoming';
  if (now > end) return 'closed';
  return 'open';
}

function formatDateTime(value) {
  if (!value) return '';
  return new Date(value).toLocaleString([], {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getStatusCopy(status, election) {
  if (status === 'open') {
    return 'Voting is open now.';
  }
  if (status === 'upcoming') {
    return `Voting opens on ${formatDateTime(election.start_at)}. You can review the candidates below.`;
  }
  if (status === 'closed') {
    return 'Voting for this election has closed.';
  }
  return '';
}

function getFileExtension(path = '') {
  return path.split('?')[0].split('.').pop()?.toLowerCase() || '';
}

export default function ElectionsPage() {
  const [election, setElection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [studentNo, setStudentNo] = useState('');
  const [selectedVotes, setSelectedVotes] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [manifestoDoc, setManifestoDoc] = useState(null);

  const status = getElectionStatus(election);
  const positions = useMemo(() => election?.positions || [], [election?.positions]);
  const votablePositions = useMemo(
    () => positions.filter((position) => (position.candidates || []).length > 0),
    [positions]
  );
  const selectedVoteCount = Object.values(selectedVotes).filter(Boolean).length;
  const totalVotes = Number(election?.vote_count || 0);

  const sortedResults = useMemo(
    () => positions.map((position) => ({
      ...position,
      candidates: [...(position.candidates || [])].sort((a, b) => Number(b.votes || 0) - Number(a.votes || 0)),
    })),
    [positions]
  );

  async function loadElection(silent = false) {
    try {
      const res = await API.get('/elections/current');
      setElection(res.data.data);
      if (!silent && !res.data.data) setMessage('No upcoming election has been published yet.');
    } catch (err) {
      console.error(err);
      if (!silent) setMessage('Unable to load the election right now.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadElection();
    const interval = setInterval(() => loadElection(true), 5000);
    return () => clearInterval(interval);
  }, []);

  async function submitVote(e) {
    e.preventDefault();
    if (status !== 'open') {
      setMessage('Voting is not open right now.');
      return;
    }
    if (selectedVoteCount !== votablePositions.length) {
      setMessage('Please select one candidate for every position before submitting.');
      return;
    }

    setSubmitting(true);
    setMessage('Submitting your vote...');
    try {
      await API.post('/elections/vote', {
        student_no: studentNo,
        votes: Object.values(selectedVotes).filter(Boolean).map((candidateId) => ({ candidate_id: candidateId })),
      });
      setStudentNo('');
      setSelectedVotes({});
      setMessage('Your vote has been recorded successfully.');
      await loadElection(true);
    } catch (err) {
      console.error(err);
      setMessage(err.response?.data?.message || 'Unable to record your vote.');
    } finally {
      setSubmitting(false);
    }
  }

  function openManifesto(candidate, event) {
    event.preventDefault();
    event.stopPropagation();
    if (!candidate.manifesto_file_path) return;
    setManifestoDoc({
      title: `${candidate.name} Manifesto`,
      fileName: candidate.manifesto_file_name || 'Manifesto document',
      url: `${API_BASE_URL}${candidate.manifesto_file_path}`,
      extension: getFileExtension(candidate.manifesto_file_path),
    });
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-32 pb-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <p className="text-sm font-semibold uppercase tracking-wide text-green-700">Student Union</p>
          <h1 className="mt-2 text-3xl font-bold text-gray-950 sm:text-4xl">
            {election?.title || 'Student Elections'}
          </h1>
          {election && (
            <div className="mt-4 flex flex-wrap gap-3 text-sm text-gray-700">
              <span className="rounded bg-white px-3 py-2 shadow-sm">Opens: {formatDateTime(election.start_at)}</span>
              <span className="rounded bg-white px-3 py-2 shadow-sm">Closes: {formatDateTime(election.end_at)}</span>
              <span className={`rounded px-3 py-2 font-semibold ${
                status === 'open' ? 'bg-green-100 text-green-800' : status === 'upcoming' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-200 text-gray-800'
              }`}>
                {status === 'open' ? 'Voting Open' : status === 'upcoming' ? 'Voting Not Started' : 'Voting Closed'}
              </span>
            </div>
          )}
        </div>

        {message && (
          <div className="mb-6 rounded border border-green-100 bg-white px-4 py-3 text-sm text-gray-800 shadow-sm">
            {message}
          </div>
        )}

        {loading ? (
          <div className="rounded bg-white p-6 shadow-sm">Loading election...</div>
        ) : !election ? (
          <div className="rounded bg-white p-6 shadow-sm">There is no published upcoming election at the moment.</div>
        ) : (
          <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr]">
            <div className="space-y-5">
              <div className={`rounded border px-4 py-3 text-sm ${
                status === 'open'
                  ? 'border-green-200 bg-green-50 text-green-900'
                  : 'border-yellow-200 bg-yellow-50 text-yellow-900'
              }`}>
                {getStatusCopy(status, election)}
              </div>

              <h2 className="text-xl font-bold text-gray-950">Ballot</h2>
              {positions.length === 0 ? (
                <div className="rounded bg-white p-6 shadow-sm">No candidates have been published yet.</div>
              ) : (
                <div className="space-y-6">
                  {positions.map((position) => (
                    <section key={position.id} className="rounded bg-white p-5 shadow-sm">
                      <h3 className="mb-4 text-lg font-bold text-gray-950">{position.title}</h3>
                      {position.candidates?.length ? (
                        <div className="grid gap-4 md:grid-cols-2">
                          {position.candidates.map((candidate) => (
                            <label
                              key={candidate.id}
                              className={`block rounded border bg-white p-4 transition ${
                                String(selectedVotes[position.id]) === String(candidate.id)
                                  ? 'border-green-600 ring-2 ring-green-100'
                                  : 'border-gray-200 hover:border-green-300'
                              }`}
                            >
                              <input
                                type="radio"
                                name={`position-${position.id}`}
                                value={candidate.id}
                                checked={String(selectedVotes[position.id] || '') === String(candidate.id)}
                                onChange={(e) => setSelectedVotes((prev) => ({ ...prev, [position.id]: e.target.value }))}
                                className="sr-only"
                                disabled={status !== 'open'}
                              />
                              <div className="flex gap-4">
                                <div className="h-20 w-20 shrink-0 overflow-hidden rounded bg-gray-100">
                                  {candidate.profile_picture ? (
                                    <img
                                      src={`${API_BASE_URL}${candidate.profile_picture}`}
                                      alt={candidate.name}
                                      className="h-full w-full object-cover"
                                    />
                                  ) : (
                                    <div className="flex h-full w-full items-center justify-center text-2xl font-bold text-gray-400">
                                      {candidate.name?.charAt(0)}
                                    </div>
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <h4 className="font-bold text-gray-950">{candidate.name}</h4>
                                  {candidate.manifesto && <p className="mt-2 text-sm leading-6 text-gray-700">{candidate.manifesto}</p>}
                                  {candidate.manifesto_file_path && (
                                    <button
                                      type="button"
                                      onClick={(event) => openManifesto(candidate, event)}
                                      className="mt-3 inline-flex rounded border border-green-600 px-3 py-1.5 text-sm font-semibold text-green-700 hover:bg-green-50"
                                    >
                                      View Manifesto
                                    </button>
                                  )}
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">No candidates for this position yet.</div>
                      )}
                    </section>
                  ))}
                </div>
              )}

              <form onSubmit={submitVote} className="rounded bg-white p-5 shadow-sm">
                <h2 className="mb-4 text-xl font-bold text-gray-950">Cast Your Vote</h2>
                {status === 'upcoming' && (
                  <p className="mb-4 text-sm text-gray-600">
                    Voting is disabled until {formatDateTime(election.start_at)}.
                  </p>
                )}
                {status === 'open' && (
                  <p className="mb-4 text-sm text-gray-600">
                    Select one candidate for every position. Each student number can submit this ballot once.
                  </p>
                )}
                <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
                  <label className="text-sm font-medium text-gray-700">
                    Student Number
                    <input
                      value={studentNo}
                      onChange={(e) => setStudentNo(e.target.value)}
                      className="mt-1 w-full rounded border border-gray-300 px-3 py-2"
                      placeholder="Enter your Student_No"
                      required
                      disabled={status !== 'open'}
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={status !== 'open' || selectedVoteCount !== votablePositions.length || submitting}
                    className="self-end rounded bg-green-600 px-5 py-2.5 font-semibold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                  >
                    {submitting ? 'Submitting...' : 'Vote'}
                  </button>
                </div>
              </form>
            </div>

            <aside className="space-y-5">
              <div className="rounded bg-white p-5 shadow-sm">
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-bold text-gray-950">Live Results</h2>
                    <p className="mt-1 text-sm text-gray-600">Grouped by position, refreshes every 5 seconds</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-green-700">{totalVotes}</div>
                    <div className="text-xs uppercase text-gray-500">total votes</div>
                  </div>
                </div>

                <div className="mt-6 space-y-6">
                  {sortedResults.map((position) => {
                    const positionTotal = position.candidates.reduce((sum, candidate) => sum + Number(candidate.votes || 0), 0);
                    return (
                      <section key={position.id} className="overflow-hidden rounded border border-gray-200 bg-gray-50">
                        <div className="flex items-center justify-between gap-3 border-b border-gray-200 bg-gray-100 px-4 py-3">
                          <div>
                            <h3 className="font-bold text-gray-950">{position.title}</h3>
                            <p className="text-xs text-gray-600">{position.candidates.length} candidates</p>
                          </div>
                          <div className="rounded bg-white px-3 py-1 text-right shadow-sm">
                            <div className="text-lg font-bold text-green-700">{positionTotal}</div>
                            <div className="text-[11px] uppercase text-gray-500">votes</div>
                          </div>
                        </div>

                        <div className="space-y-3 p-4">
                          {position.candidates.map((candidate, index) => {
                            const votes = Number(candidate.votes || 0);
                            const percent = positionTotal ? Math.round((votes / positionTotal) * 100) : 0;
                            return (
                              <div key={candidate.id} className="rounded bg-white p-3 shadow-sm">
                                <div className="mb-2 flex items-center justify-between gap-3 text-sm">
                                  <div className="flex min-w-0 items-center gap-2">
                                    <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                                      index === 0 && votes > 0 ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                      {index + 1}
                                    </span>
                                    <span className="truncate font-semibold text-gray-800">{candidate.name}</span>
                                  </div>
                                  <span className="shrink-0 text-gray-600">{votes} ({percent}%)</span>
                                </div>
                                <div className="h-3 overflow-hidden rounded bg-gray-100">
                                  <div
                                    className={`h-full rounded transition-all duration-500 ${
                                      index === 0 && votes > 0 ? 'bg-green-600' : 'bg-gray-400'
                                    }`}
                                    style={{ width: `${percent}%` }}
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </section>
                    );
                  })}
                </div>
              </div>
            </aside>
          </div>
        )}
      </div>

      {manifestoDoc && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-gray-950/70 px-4 py-6">
          <div className="flex h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded bg-white shadow-2xl">
            <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
              <div className="min-w-0">
                <h2 className="truncate text-lg font-bold text-gray-950">{manifestoDoc.title}</h2>
                <p className="truncate text-sm text-gray-600">{manifestoDoc.fileName}</p>
              </div>
              <button
                type="button"
                onClick={() => setManifestoDoc(null)}
                className="rounded border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>

            <div className="min-h-0 flex-1 bg-gray-100">
              {manifestoDoc.extension === 'pdf' ? (
                <iframe
                  title={manifestoDoc.title}
                  src={manifestoDoc.url}
                  className="h-full w-full"
                />
              ) : (
                <iframe
                  title={manifestoDoc.title}
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(manifestoDoc.url)}`}
                  className="h-full w-full"
                />
              )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 px-4 py-3 text-sm text-gray-600">
              <span>Word previews require the document URL to be publicly accessible.</span>
              <a
                href={manifestoDoc.url}
                target="_blank"
                rel="noreferrer"
                className="font-semibold text-green-700 hover:text-green-800"
              >
                Open document in new tab
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
